/**
 * AudiencePlotMessage — the room display (Step 11, SPEC section 3): one
 * audience chart posted by the Research Assistant, drawn through deck-ui
 * (`ChoicePlotView` for choice bars, the analyst tab's `AnalystPlot` for the
 * other kinds) in a message bubble (the plot sits inside the assistant's bubble, no card of its own).
 *
 * The chart is the full original audience. When the message carries
 * `roomPlot`, two pills switch to the room's twins only, shown as counts for
 * every kind that has a count unit (choice bars, the histogram); a ranking
 * keeps its scores, and the caption names the room's twin count.
 * The switch is client-side, per viewer and per message, never persisted.
 *
 * The card opens as a peek: the header plus the top of the chart under a fade.
 * The chevron (or the fade) expands it; the chevron collapses it back to the
 * peek. The pills sit under the title and show peeked or open. The open state is remembered per
 * viewer and per message in localStorage (`room-plot-open:<messageId>`).
 */

import { MouseEvent, TransitionEvent, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'lucide-react'

import { ChoiceBarsPlotData, ChoicePlotView } from '@digital-twins/deck-ui/plots/choice-bars'
import { AnalystPlot, hasAnalystPlotView } from '@/features/studies/report/page/components/tabs/AnalystTab/AnalystPlot'
import { readStoredOpen, writeStoredOpen } from '@/lib/storage/storedOpen'
import { cn } from '@/lib/utils'

import { AnalystChartPlot, AudiencePlotMetadata, AudiencePlotView } from '@digital-twins/shared-types'

type PlotBase = 'audience' | 'room'

const OPEN_STORAGE_PREFIX = 'room-plot-open:'
/** The chart height left visible while peeking. */
const PEEK_HEIGHT = '72px'
const FULL_HEIGHT = 'none'
/** Fades the peeked chart into the bubble behind it, whatever the bubble color. */
const PEEK_MASK = 'linear-gradient(to bottom, black 35%, transparent)'

interface AudiencePlotMessageProps {
  messageId: string
  metadata: AudiencePlotMetadata
}

interface ChartCollapseToggleProps {
  isOpen: boolean
  controlsId: string
  label: string
  dataTestId: string
  /** The card body is hovered: nudge the chevron toward where a click on the body leads. */
  isHinting?: boolean
  onToggle: () => void
}

/** The chevron that expands the peeked chart and collapses it back to the peek. */
export function ChartCollapseToggle(props: ChartCollapseToggleProps) {
  const { isOpen, controlsId, label, dataTestId, isHinting = false, onToggle } = props

  const Chevron = isOpen ? ChevronUp : ChevronDown

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={controlsId}
      aria-label={label}
      title={label}
      data-testid={dataTestId}
      className={cn(
        'flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors duration-300 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-twin-chat-accent',
        isHinting ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      <Chevron
        className={cn(
          'h-4 w-4 transition-transform duration-300 ease-out motion-reduce:transition-none',
          isHinting && (isOpen ? '-translate-y-0.5' : 'translate-y-0.5'),
        )}
        aria-hidden="true"
      />
    </button>
  )
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isPlotView(value: unknown): value is AudiencePlotView {
  return isRecord(value) && isRecord(value.chart) && isRecord(value.base) && typeof value.base.n === 'number'
}

/** Shape guard for a stored `audience_plot` metadata (C10); never trusts the blob blindly. */
export function isAudiencePlotMetadata(value: unknown): value is AudiencePlotMetadata {
  if (!isRecord(value) || typeof value.label !== 'string' || !isPlotView(value)) {
    return false
  }
  return value.roomPlot === undefined || value.roomPlot === null || isPlotView(value.roomPlot)
}

/** George's note stored beside the chart (`displayNote`, e.g. the split note), else null. */
export function readDisplayNote(metadata: unknown): string | null {
  if (!isRecord(metadata) || typeof metadata.displayNote !== 'string') {
    return null
  }
  const note = metadata.displayNote.trim()
  return note.length > 0 ? note : null
}

function drawablePlot(view: AudiencePlotView): AnalystChartPlot | null {
  const plot = view.chart.plot
  if (!isRecord(plot) || typeof plot.kind !== 'string' || !isRecord(plot.data) || !hasAnalystPlotView(plot.kind)) {
    return null
  }
  return plot
}

export function AudiencePlotMessage(props: AudiencePlotMessageProps) {
  const { messageId, metadata } = props

  const openStorageKey = `${OPEN_STORAGE_PREFIX}${messageId}`

  const [base, setBase] = useState<PlotBase>('audience')
  const [isOpen, setIsOpen] = useState(() => readStoredOpen(openStorageKey) ?? false)
  const [isBodyHovered, setIsBodyHovered] = useState(false)
  const [bodyMaxHeight, setBodyMaxHeight] = useState(() => (isOpen ? FULL_HEIGHT : PEEK_HEIGHT))

  const { t, i18n } = useTranslation('app')
  const bodyId = useId()
  const bodyRef = useRef<HTMLDivElement>(null)

  const onSetOpen = (next: boolean) => {
    setIsOpen(next)
    writeStoredOpen(openStorageKey, next)
    const body = bodyRef.current
    // No animation when motion is reduced or the chart already fits the peek (no transitionend would fire).
    if (!body || prefersReducedMotion() || body.scrollHeight <= body.clientHeight) {
      setBodyMaxHeight(next ? FULL_HEIGHT : PEEK_HEIGHT)
      return
    }
    if (next) {
      // Animate from the peek to the measured height, then release the cap (onTransitionEnd).
      setBodyMaxHeight(`${body.scrollHeight}px`)
      return
    }
    // Pin the current height first so the collapse animates from it, not from `none`.
    body.style.maxHeight = `${body.scrollHeight}px`
    void body.offsetHeight
    setBodyMaxHeight(PEEK_HEIGHT)
  }

  const onToggleOpen = () => onSetOpen(!isOpen)

  // The open body toggles as a whole; controls inside it (unit pills, links) and a text
  // selection keep their own meaning. The peek already opens through its fade button.
  const onBodyClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    if (isPeeking || target.closest('button, a, input, select, [role="button"]')) {
      return
    }
    if (window.getSelection()?.toString()) {
      return
    }
    onSetOpen(false)
  }

  const onBodyTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && event.propertyName === 'max-height' && isOpen) {
      setBodyMaxHeight(FULL_HEIGHT)
    }
  }

  const roomView = isPlotView(metadata.roomPlot) ? metadata.roomPlot : null
  const isRoomView = base === 'room' && roomView !== null
  const view: AudiencePlotView = isRoomView ? roomView : metadata
  const plot = drawablePlot(view)
  const caption = isRoomView
    ? t('roomThread.chartCaptionRoom', { count: view.base.n })
    : t('roomThread.chartCaption', { count: view.base.n })
  const testIdScope = `audience-plot-${messageId}`
  const note = readDisplayNote(metadata)

  // The look of deck-ui's `plot-alternative-toggle` pills.
  const pillClass = (isActive: boolean) =>
    cn(
      'rounded-md border px-2 py-0.5 text-xs',
      isActive
        ? 'border-border bg-muted text-foreground'
        : 'border-transparent text-muted-foreground hover:text-foreground',
    )

  const isPeeking = plot !== null && !isOpen

  // Only when the room view exists, so both chips always work; always shown, peek or open.
  const toggle = roomView ? (
    <div role="group" aria-label={t('roomThread.chartBaseGroup')} className="mt-2 flex shrink-0 gap-1">
      <button
        type="button"
        aria-pressed={!isRoomView}
        className={pillClass(!isRoomView)}
        onClick={() => setBase('audience')}
        data-testid={`${testIdScope}-base-audience`}
      >
        {t('roomThread.baseAudience')}
      </button>
      <button
        type="button"
        aria-pressed={isRoomView}
        className={pillClass(isRoomView)}
        onClick={() => setBase('room')}
        data-testid={`${testIdScope}-base-room`}
      >
        {t('roomThread.baseRoom')}
      </button>
    </div>
  ) : null

  const shareUnit = isRoomView ? 'count' : undefined
  // Keyed by base: a kind view with its own percent / count toggle reopens on the base's unit.
  const chart = !plot ? null : plot.kind === 'choice-bars' ? (
    <ChoicePlotView
      key={base}
      data={plot.data as unknown as ChoiceBarsPlotData}
      locale={i18n.language}
      shareUnit={shareUnit}
      testIdPrefix={`${testIdScope}-choice`}
    />
  ) : (
    <AnalystPlot
      key={base}
      plot={plot}
      index={0}
      isLegacy={false}
      testIdScope={testIdScope}
      shareUnit={shareUnit}
    />
  )

  return (
    <figure className="w-full" data-testid={testIdScope}>
      <figcaption className="flex items-start justify-between gap-3">
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-medium text-twin-chat-fg">{metadata.label}</span>
          <span className="text-xs text-muted-foreground">{caption}</span>
          {toggle}
        </span>
        {chart && (
          <ChartCollapseToggle
            isOpen={isOpen}
            controlsId={bodyId}
            label={isOpen ? t('roomThread.chartCollapse') : t('roomThread.chartExpand')}
            dataTestId={`${testIdScope}-toggle`}
            isHinting={isBodyHovered}
            onToggle={onToggleOpen}
          />
        )}
      </figcaption>
      {chart && (
        <div
          id={bodyId}
          ref={bodyRef}
          className={cn(
            'relative mt-4 min-h-0 overflow-hidden transition-[max-height] duration-300 ease-out motion-reduce:transition-none',
            isOpen && 'cursor-pointer',
          )}
          style={{
            maxHeight: bodyMaxHeight,
            ...(isPeeking ? { maskImage: PEEK_MASK, WebkitMaskImage: PEEK_MASK } : null),
          }}
          onClick={onBodyClick}
          onMouseEnter={() => setIsBodyHovered(true)}
          onMouseLeave={() => setIsBodyHovered(false)}
          onTransitionEnd={onBodyTransitionEnd}
          data-testid={`${testIdScope}-body`}
        >
          {chart}
          {isPeeking && (
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              onClick={() => onSetOpen(true)}
              className="absolute inset-x-0 bottom-0 h-full cursor-pointer"
              data-testid={`${testIdScope}-fade`}
            />
          )}
        </div>
      )}
      {note && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid={`${testIdScope}-note`}>
          {note}
        </p>
      )}
    </figure>
  )
}
